import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Drawer } from '@mui/joy';
import { Menu as MenuIcon, Close } from '@mui/icons-material';
import { authApi } from '../services/api';
import '../styles/agentql.css';

interface AppShellProps {
  children: React.ReactNode;
  showNav?: boolean;
}

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', match: (path: string) => path === '/' },
  {
    to: '/statistics',
    label: 'Statistics',
    match: (path: string) => path.startsWith('/statistics'),
  },
  {
    to: '/component-styles',
    label: 'Styles',
    match: (path: string) =>
      path.startsWith('/component-styles') || path.startsWith('/component-style-groups'),
  },
  { to: '/settings', label: 'Settings', match: (path: string) => path.startsWith('/settings') },
];

export const AppHeader: React.FC<{ showNav?: boolean }> = ({ showNav = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    window.location.href = '/login';
  };

  const go = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <header className="admin-header">
      <div className="admin-header__bar">
        <RouterLink to="/" className="admin-header__brand">
          <span className="admin-header__brand-full">Payment Form Constructor</span>
          <span className="admin-header__brand-short">PFC</span>
        </RouterLink>

        {showNav && (
          <>
            <nav className="admin-header__nav admin-header__nav--desktop" aria-label="Primary">
              {NAV_ITEMS.map((item) => {
                const active = item.match(location.pathname);
                return (
                  <button
                    key={item.to}
                    type="button"
                    className={`admin-header__link${active ? ' is-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => navigate(item.to)}
                  >
                    {item.label}
                  </button>
                );
              })}
              <button type="button" className="admin-header__cta" onClick={handleLogout}>
                Logout
              </button>
            </nav>

            <IconButton
              className="admin-header__menu-btn"
              variant="outlined"
              color="neutral"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                borderRadius: '8px',
                borderColor: 'var(--color-obsidian-edge)',
                minWidth: 44,
                minHeight: 44,
              }}
            >
              {menuOpen ? <Close /> : <MenuIcon />}
            </IconButton>
          </>
        )}
      </div>

      {showNav && (
        <Drawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          anchor="right"
          size="sm"
          sx={{
            display: { xs: 'block', md: 'none' },
            '--Drawer-horizontalSize': 'min(320px, 88vw)',
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              height: '100%',
              bgcolor: 'var(--color-abyss)',
            }}
          >
            <Typography
              level="title-sm"
              sx={{ color: 'var(--color-ash)', mb: 1, fontWeight: 500 }}
            >
              Menu
            </Typography>
            {NAV_ITEMS.map((item) => {
              const active = item.match(location.pathname);
              return (
                <button
                  key={item.to}
                  type="button"
                  className={`admin-header__link admin-header__link--drawer${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => go(item.to)}
                >
                  {item.label}
                </button>
              );
            })}
            <Box sx={{ flex: 1 }} />
            <button type="button" className="admin-header__cta admin-header__cta--block" onClick={handleLogout}>
              Logout
            </button>
          </Box>
        </Drawer>
      )}
    </header>
  );
};

export const AppFooter: React.FC = () => (
  <footer className="admin-footer">
    <Typography level="body-xs" sx={{ color: 'inherit', fontWeight: 300 }}>
      Payment Form Constructor
    </Typography>
    <Typography level="body-xs" sx={{ color: 'inherit', fontWeight: 300 }}>
      Admin console · structured &amp; HTML forms
    </Typography>
  </footer>
);

export const AppShell: React.FC<AppShellProps> = ({ children, showNav = true }) => (
  <Box className="admin-shell">
    <AppHeader showNav={showNav} />
    <Box className="admin-shell__main" component="main">
      {children}
    </Box>
    <AppFooter />
  </Box>
);

export default AppShell;
